import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, MapPin, CalendarDays, ImagePlus, Eye, EyeOff, Euro, DownloadCloud } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import MapPointPicker from '../MapPointPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { slug: 'kurzeme', name: 'Kurzeme' },
  { slug: 'vidzeme', name: 'Vidzeme' },
  { slug: 'zemgale', name: 'Zemgale' },
  { slug: 'latgale', name: 'Latgale' },
  { slug: 'seliija', name: 'Sēlija' },
  { slug: 'riga', name: 'Rīga' },
];

const CATEGORIES = [
  { key: 'culture_art', lv: 'Kultūra & Māksla' },
  { key: 'music', lv: 'Mūzika' },
  { key: 'sports_active', lv: 'Sports & Aktīvā atpūta' },
  { key: 'family', lv: 'Ģimenei' },
  { key: 'food_drink', lv: 'Ēdiens & Dzērieni' },
  { key: 'business_networking', lv: 'Bizness & Tīklošanās' },
  { key: 'nightlife', lv: 'Naktsdzīve' },
  { key: 'charity', lv: 'Labdarība' },
  { key: 'wedding', lv: 'Kāzas' },
  { key: 'other', lv: 'Cits' },
];

const EMPTY = {
  title: '', description: '', category: 'culture_art', region: 'kurzeme',
  city: '', location_name: '', address: '', event_date: '', end_date: '',
  image_url: '', lat: null, lng: null, is_paid: false, price: '', external_url: '',
};

export default function EventsAdminTab() {
  const [data, setData] = useState({ active: [], ended: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/events/admin/list`); setData(data); }
    catch (e) { toast.error('Neizdevās ielādēt'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch (e) { toast.error('Augšupielāde neizdevās'); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Ievadi nosaukumu'); return; }
    if (form.lat == null || form.lng == null) { toast.error('Norādi vietu kartē'); return; }
    if (!form.event_date) { toast.error('Norādi sākuma datumu'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/events`, {
        title: form.title, description: form.description, category: form.category,
        region: form.region, city: form.city || null,
        lat: form.lat, lng: form.lng,
        event_date: new Date(form.event_date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        location_name: form.location_name || null, address: form.address || null,
        image_url: form.image_url || null, is_public: true,
        is_paid: form.is_paid, price: form.is_paid && form.price ? Number(form.price) : null,
        external_url: form.external_url || null, slots: [],
      });
      toast.success('Pasākums izveidots un publicēts!');
      setForm(EMPTY); setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Neizdevās izveidot'); } finally { setSaving(false); }
  };

  const setActive = async (id, active) => {
    try { await axios.post(`${API}/events/${id}/set-active`, { active }); toast.success(active ? 'Aktivizēts' : 'Deaktivizēts'); load(); }
    catch (e) { toast.error('Neizdevās'); }
  };
  const del = async (id) => {
    if (!window.confirm('Dzēst pasākumu neatgriezeniski?')) return;
    try { await axios.delete(`${API}/events/${id}`); toast.success('Dzēsts'); load(); }
    catch (e) { toast.error('Neizdevās'); }
  };

  const importKurzeme = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/events/import/kurzeme`, {}, { timeout: 90000 });
      toast.success(`Importēti: ${data.imported} jauni, ${data.updated} atjaunināti (Liepājas kalendārs)`);
      load();
    } catch (e) { toast.error('Imports neizdevās'); } finally { setImporting(false); }
  };

  const Row = ({ ev, ended }) => (
    <div className="flex items-center gap-4 p-4 bg-white border border-[#E5E4E0] rounded-xl" data-testid={`event-row-${ev.id}`}>
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F3F2EE] flex items-center justify-center shrink-0">
        {ev.image_url ? <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" /> : <CalendarDays className="w-6 h-6 text-[#C4C3BE]" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#121212] truncate">{ev.title}</p>
        <p className="text-xs text-[#5C5C5C] flex items-center gap-2 mt-0.5">
          <MapPin className="w-3 h-3" /> {ev.location_name || ev.city || ev.region}
          <span>· {ev.event_date ? new Date(ev.event_date).toLocaleString('lv-LV', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
          {ev.is_paid ? <span className="text-[#E11D48] font-semibold">· €{ev.price}</span> : <span className="text-green-600 font-semibold">· Bezmaksas</span>}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {ended ? (
          <Button size="sm" variant="outline" onClick={() => setActive(ev.id, true)} data-testid={`event-activate-${ev.id}`}>
            <Eye className="w-4 h-4 mr-1" /> Aktivizēt
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setActive(ev.id, false)} data-testid={`event-deactivate-${ev.id}`}>
            <EyeOff className="w-4 h-4 mr-1" /> Slēpt
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(ev.id)} data-testid={`event-delete-${ev.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div data-testid="events-admin-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-outfit text-2xl font-bold text-[#121212]">Latvijas pasākumi</h2>
          <p className="text-sm text-[#5C5C5C]">Publicē bezmaksas (un maksas) pasākumus, kas parādās kartē zem "Pasākumi".</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={importKurzeme} disabled={importing} variant="outline" className="border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53]/5" data-testid="event-import-kurzeme-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DownloadCloud className="w-4 h-4 mr-1" />} Importēt Kurzemes bezmaksas
          </Button>
          <Button onClick={() => setShowForm((s) => !s)} className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="event-new-btn">
            <Plus className="w-4 h-4 mr-1" /> {showForm ? 'Aizvērt' : 'Jauns pasākums'}
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 mb-8 space-y-4" data-testid="event-create-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Nosaukums *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Piem. Kurzemes Zvejnieku svētki" data-testid="event-title-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Apraksts</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="event-desc-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Kategorija</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-11 rounded-lg border border-[#E5E4E0] px-3 bg-white text-sm" data-testid="event-category-select">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.lv}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Reģions</Label>
              <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="w-full h-11 rounded-lg border border-[#E5E4E0] px-3 bg-white text-sm" data-testid="event-region-select">
                {REGIONS.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Pilsēta / ciems</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ventspils" data-testid="event-city-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Vietas nosaukums</Label>
              <Input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} placeholder="Ostas laukums" data-testid="event-location-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Sākums *</Label>
              <Input type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Beigas</Label>
              <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="event-end-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Adrese</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Ostas iela 1, Ventspils" data-testid="event-address-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_paid} onChange={(e) => setForm({ ...form, is_paid: e.target.checked })} data-testid="event-paid-checkbox" />
                Maksas pasākums
              </Label>
              {form.is_paid && (
                <div className="relative">
                  <Euro className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                  <Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="pl-9" placeholder="Cena" data-testid="event-price-input" />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Ārējā saite (biļetes / info)</Label>
              <Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://..." data-testid="event-url-input" />
            </div>
          </div>

          {/* Image */}
          <div className="space-y-2">
            <Label>Attēls</Label>
            <div className="flex items-center gap-4">
              <div className="w-28 h-20 rounded-lg overflow-hidden bg-[#F3F2EE] flex items-center justify-center">
                {form.image_url ? <img src={form.image_url} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-[#C4C3BE]" />}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-[#D97736] hover:underline">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="event-image-upload" />
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                {form.image_url ? 'Mainīt attēlu' : 'Augšupielādēt attēlu'}
              </label>
            </div>
          </div>

          {/* Map location */}
          <div className="space-y-2">
            <Label>Precīza atrašanās vieta kartē * <span className="text-xs text-[#5C5C5C]">(klikšķini kartē)</span></Label>
            <div className="h-64 rounded-xl overflow-hidden border border-[#E5E4E0]">
              <MapPointPicker lat={form.lat} lng={form.lng} onChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))} />
            </div>
            {form.lat != null && <p className="text-xs text-[#5C5C5C]">📍 {form.lat}, {form.lng}</p>}
          </div>

          <Button onClick={submit} disabled={saving} className="w-full bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="event-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Publicēt pasākumu
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#D97736]" /></div>
      ) : (
        <div className="space-y-8">
          <div>
            <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-3">Aktīvie ({data.active.length})</h3>
            {data.active.length === 0 ? <p className="text-sm text-[#5C5C5C]">Nav aktīvu pasākumu.</p> :
              <div className="space-y-3" data-testid="active-events-list">{data.active.map((ev) => <Row key={ev.id} ev={ev} ended={false} />)}</div>}
          </div>
          <div>
            <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-3">Beigušies / neaktīvi ({data.ended.length})</h3>
            {data.ended.length === 0 ? <p className="text-sm text-[#5C5C5C]">Nav.</p> :
              <div className="space-y-3 opacity-75" data-testid="ended-events-list">{data.ended.map((ev) => <Row key={ev.id} ev={ev} ended={true} />)}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
